import { EARLY_ACCESS_ENDPOINT } from "../shared";

/**
 * What day 8 sends. The email is typed; everything else is context the app
 * already holds, and the form says so in plain words before asking. Knowing
 * which pair somebody was learning, and how far they got before they hit the
 * wall, is the difference between a list of addresses and a list worth reading.
 */
export interface EarlyAccessSignup {
  email: string;
  /** Human-readable pair, e.g. "Thai to Dutch". */
  languages: string;
  /** The day they were standing on when they were asked. */
  day: number;
  words: number;
  sentences: number;
  /** Formspree drops a submission when its honeypot field is filled. */
  honeypot?: string;
}

export class EarlyAccessError extends Error {
  constructor(
    message: string,
    /** False when the list was never reached, so nothing was recorded. */
    readonly reached: boolean,
  ) {
    super(message);
    this.name = "EarlyAccessError";
  }
}

export function isEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}

export async function submitEarlyAccess(
  signup: EarlyAccessSignup,
): Promise<void> {
  let response: Response;

  try {
    response = await fetch(EARLY_ACCESS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        email: signup.email.trim(),
        languages: signup.languages,
        day: signup.day,
        pace: `${signup.words} words and ${signup.sentences} sentences a day`,
        _subject: `SprinkleDays early list: ${signup.languages}`,
        _gotcha: signup.honeypot ?? "",
      }),
    });
  } catch {
    throw new EarlyAccessError(
      "We could not reach the list. Check your connection and try again.",
      false,
    );
  }

  if (!response.ok) {
    throw new EarlyAccessError(
      response.status === 422
        ? "That address was refused. Check it for typos and try again."
        : "The list did not accept that just now. Please try again in a moment.",
      true,
    );
  }
}
